import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Card, Typography, Alert, Badge } from 'components/base';
import { capitalizeString } from 'utils/format';
import get from 'utils/get';
import { getOptionImageSrcSet } from 'data/lib/product';
import OptionImage from './optionImage';

const ConfigurationSection = props => {
  const handleOnChange = optionId => {
    props.onChange(props.id, optionId);
  };

  const numberOfItems = props.options.length + props.unavailable.length;

  if (numberOfItems < props.minNumberOfItems) {
    return null;
  }

  const hasOptionDescriptions = props.options.some(option => option.description);

  let itemsInRow;
  if (props.autoItemsInRow) {
    itemsInRow = 'auto';
  } else {
    switch (numberOfItems) {
      case 1:
        itemsInRow = 1;
        break;
      case 2:
        itemsInRow = 2;
        break;
      case 3:
        itemsInRow = 3;
        break;
      case 4:
        itemsInRow = 2;
        break;
      default:
        itemsInRow = 'auto';
    }
  }

  const description = get(props.description, `${props.id}.${props.selectedOptionId}`);

  return (
    <div className="configuration-row">
      <Typography.Span block className="label text-light">
        {props.label.toUpperCase()}
      </Typography.Span>
      <div className="card-list" data-items-in-row={itemsInRow}>
        {props.options.map(option => (
          <Card
            key={option.id}
            className={classNames({
              active: option.id === props.selectedOptionId,
              [props.cardClassName]: props.cardClassName,
              'card-disabled': option.disabled,
            })}
            {...(option.disabled
              ? {
                asLink: false,
              }
              : {
                asLink: true,
                onClick: e => {
                  e.preventDefault();
                  handleOnChange(option.id);
                },
              })}
          >
            <div
              className={classNames('card-inline h-100', {
                'align-items-center': !hasOptionDescriptions,
                // Descriptions wrap to different heights, so labels line up at the top
                'align-items-start': hasOptionDescriptions,
              })}
            >
              {props.id.toLowerCase().indexOf('color') >= 0 && (
                <OptionImage
                  className="rounded shadow-sm"
                  srcSet={getOptionImageSrcSet(props.templateRootPath, 'color', option.id)}
                  alt={`${props.label}: ${option.label}`}
                />
              )}
              <div
                className={classNames({
                  'w-100': true,
                  'h6 text-center': !hasOptionDescriptions,
                  'text-light': option.disabled,
                })}
              >
                <div className={classNames({ h6: hasOptionDescriptions })}>
                  {capitalizeString(option.label)}

                  {/** HARDCODE, because this is soooo complex. */}
                  {option.label === "Fan" && (
                    <>
                      {" "}
                      <Badge type="success" size="sm" className="mt-0 text-white badge-config-sm">
                        NEW
                      </Badge>
                    </>
                  )}
                </div>
                {option.description && (
                  <Typography.Span block className="small text-light">
                    {option.description}
                  </Typography.Span>
                )}
              </div>
            </div>
          </Card>
        ))}
        {props.unavailable.map(option => (
          <Card
            key={option.label}
            className={classNames({
              [props.cardClassName]: props.cardClassName,
              'card-disabled': true,
            })}
          >
            <div className="card-inline h-100 align-items-center">
              <div className="h6 text-center w-100 text-light">
                {capitalizeString(option.label)}{' '}
                <Badge type="secondary" size="sm" className="mt-0 text-white badge-config-sm">
                  {option.type.toUpperCase()}
                </Badge>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {description && (
        <Alert type="warning">
          <div dangerouslySetInnerHTML={{ __html: description }} />
        </Alert>
      )}
    </div>
  );
};

ConfigurationSection.propTypes = {
  id: PropTypes.string,
  templateRootPath: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  unavailable: PropTypes.array,
  selectedOptionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  cardClassName: PropTypes.string,
  minNumberOfItems: PropTypes.number,
  autoItemsInRow: PropTypes.bool,
  description: PropTypes.object,
};

ConfigurationSection.defaultProps = {
  options: [],
  unavailable: [],
  description: {},
  minNumberOfItems: 1,
  autoItemsInRow: false,
};

export default ConfigurationSection;
